import type { WaitSoundVolume } from "@/types/ai-assistant-style";
import type { BreathPlayer } from "./voice-breath-player";

/**
 * Pengatur bunyi tunggu mode suara (#60): napas diputar hanya bila giliran sudah
 * berjalan `WAIT_SOUND_DELAY_MS` tanpa kalimat siap/terdengar, dan berhenti begitu
 * audio GPT-Live mulai, barge-in, bisukan, atau Off. Bunyinya tidak pernah masuk
 * teks, riwayat, maupun transkrip.
 */

/** Tunggu ini setelah delegasi dimulai sebelum napas diputar (±0,6 dtk). */
export const WAIT_SOUND_DELAY_MS = 600;

export interface WaitSoundSettings {
	enabled: boolean;
	volume: WaitSoundVolume;
}

/** Default sebelum setelan server tiba — mati, sama dengan default admin (eksperimen #62). */
export const DEFAULT_WAIT_SOUND: WaitSoundSettings = {
	enabled: false,
	volume: "sedang",
};

export interface WaitSoundOptions {
	player: BreathPlayer;
	/** Bunyi mulai/berhenti — dipakai penyaring gema agar menutup periode bunyi + ekornya. */
	onActive: (active: boolean, nowMs: number) => void;
	now: () => number;
	/** Jadwalkan `fn` setelah `ms`; mengembalikan pembatal. Default: setTimeout. */
	schedule?: (fn: () => void, ms: number) => () => void;
	delayMs?: number;
}

export interface WaitSound {
	/** Delegasi dimulai: jadwalkan napas bila belum ada kalimat siap/terdengar. */
	arm(): void;
	/** Kalimat pertama siap dikirim: batalkan napas yang belum mulai (yang berjalan dibiarkan sampai audio terdengar). */
	sentenceReady(): void;
	/** Audio GPT-Live terdengar: hentikan napas. */
	audible(): void;
	/** Barge-in, giliran selesai/gagal: hentikan semuanya. */
	cancel(): void;
	setMuted(muted: boolean): void;
	configure(settings: WaitSoundSettings): void;
	/** Buat/lanjutkan AudioContext dari klik On. */
	prime(): void;
	close(): void;
}

const defaultSchedule = (fn: () => void, ms: number) => {
	const id = setTimeout(fn, ms);
	return () => clearTimeout(id);
};

export function createWaitSound(opts: WaitSoundOptions): WaitSound {
	const { player, onActive, now } = opts;
	const schedule = opts.schedule ?? defaultSchedule;
	const delayMs = opts.delayMs ?? WAIT_SOUND_DELAY_MS;
	let settings = DEFAULT_WAIT_SOUND;
	let muted = false;
	let cancelPending: (() => void) | null = null;
	let active = false;

	const setActive = (value: boolean) => {
		if (active === value) return;
		active = value;
		onActive(value, now());
	};
	const clearPending = () => {
		cancelPending?.();
		cancelPending = null;
	};
	const stopAll = () => {
		clearPending();
		if (!active) return;
		player.stop();
		setActive(false);
	};

	return {
		arm() {
			stopAll();
			if (!settings.enabled || muted) return;
			cancelPending = schedule(() => {
				cancelPending = null;
				setActive(true);
				player.play(settings.volume, () => setActive(false));
			}, delayMs);
		},
		sentenceReady: clearPending,
		audible: stopAll,
		cancel: stopAll,
		setMuted(value) {
			muted = value;
			if (value) stopAll();
		},
		configure(next) {
			settings = next;
			if (!next.enabled) stopAll();
		},
		prime: () => player.prime(),
		close() {
			stopAll();
			player.close();
		},
	};
}
